import { CheckCircle2, Circle, PlayCircle, ListChecks, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const ChecklistTab = ({ checklist, checklistLoading, handleChecklistItemUpdate, handleResetChecklist }) => (
  <TabsContent value="checklist" className="animate-fade-in">
    <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-[#FF4500]" /> Project Workflow Checklist
          </h3>
          {checklist?.progress && (
            <p className="text-sm text-gray-500 mt-1">
              {checklist.progress.completed}/{checklist.progress.total} completed ({checklist.progress.percent}%)
            </p>
          )}
        </div>
        <Button variant="outline" onClick={handleResetChecklist} className="border-[#262626]">
          <RefreshCw className="w-4 h-4 mr-2" /> Reset
        </Button>
      </div>

      {/* Progress Bar */}
      {checklist?.progress && (
        <div className="mb-6">
          <div className="h-3 bg-[#262626] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#FF4500] to-[#F59E0B] transition-all duration-500"
              style={{ width: `${checklist.progress.percent}%` }}
            />
          </div>
        </div>
      )}

      {checklistLoading ? (
        <div className="space-y-3">
          {[1,2,3,4,5].map(i => <div key={i} className="h-12 skeleton"></div>)}
        </div>
      ) : checklist?.items?.length > 0 ? (
        <div className="space-y-6">
          {["structure", "interior", "exterior", "services"].map(category => {
            const categoryItems = checklist.items.filter(item => item.category === category);
            if (categoryItems.length === 0) return null;

            const categoryCompleted = categoryItems.filter(i => i.status === "completed").length;
            const categoryPercent = Math.round((categoryCompleted / categoryItems.length) * 100);

            return (
              <div key={category} className="border border-[#262626] rounded overflow-hidden">
                <div className={`p-4 flex items-center justify-between ${
                  category === 'structure' ? 'bg-[#3B82F6]/10' :
                  category === 'interior' ? 'bg-[#A78BFA]/10' :
                  category === 'exterior' ? 'bg-[#10B981]/10' :
                  'bg-[#F59E0B]/10'
                }`}>
                  <h4 className="font-semibold text-white capitalize flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${
                      category === 'structure' ? 'bg-[#3B82F6]' :
                      category === 'interior' ? 'bg-[#A78BFA]' :
                      category === 'exterior' ? 'bg-[#10B981]' :
                      'bg-[#F59E0B]'
                    }`} />
                    {category}
                  </h4>
                  <span className="text-sm text-gray-400">
                    {categoryCompleted}/{categoryItems.length} ({categoryPercent}%)
                  </span>
                </div>
                <div className="divide-y divide-[#262626]">
                  {categoryItems.map((item) => (
                    <div key={item.id} className={`p-4 flex items-center gap-4 hover:bg-[#171717] transition-colors ${item.status === 'completed' ? 'opacity-60' : ''}`}>
                      <button
                        onClick={() => handleChecklistItemUpdate(item.id, item.status === 'completed' ? 'pending' : 'completed')}
                        className="flex-shrink-0"
                      >
                        {item.status === 'completed' ? (
                          <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
                        ) : item.status === 'in_progress' ? (
                          <PlayCircle className="w-6 h-6 text-[#F59E0B]" />
                        ) : (
                          <Circle className="w-6 h-6 text-gray-500 hover:text-[#FF4500]" />
                        )}
                      </button>
                      <div className="flex-1">
                        <p className={`font-medium ${item.status === 'completed' ? 'text-gray-500 line-through' : 'text-white'}`}>
                          {item.item}
                        </p>
                        <p className="text-xs text-gray-500">{item.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.status !== 'completed' && (
                          <Select
                            value={item.status}
                            onValueChange={(value) => handleChecklistItemUpdate(item.id, value)}
                          >
                            <SelectTrigger className="w-32 bg-[#171717] border-[#262626] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        {item.completed_date && (
                          <span className="text-xs text-gray-500">
                            {new Date(item.completed_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <ListChecks className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">No checklist items</p>
        </div>
      )}
    </div>
  </TabsContent>
);
